import { useState } from 'react';
import type { DashboardData, Issue } from '@/types';
import { IssueDonutChart } from './IssueDonutChart';
import { IssuesCalendarGrid } from './IssuesCalendarGrid';
import { IssuesCalendarEvents } from './IssuesCalendarEvents';
import { useProjectStore } from '@/stores';
import { useIssueStore } from '@/stores';

interface CalendarEventItem {
  issueId: string;
  issueSequence: number;
  issueDescription: string;
  eventType: 'task' | 'st-breakpoint' | 'lt-breakpoint';
  eventDate: string;
  owner: string;
}

interface IssuesDashboardProps {
  data: DashboardData | null;
  productIssues: Issue[];
  productionIssues: Issue[];
}

export function IssuesDashboard({ data, productIssues, productionIssues }: IssuesDashboardProps) {
  const { currentProject } = useProjectStore();
  const { setActiveView, setExpandedIssueId } = useIssueStore();
  const allIssues = [...productIssues, ...productionIssues];

  const today = new Date();
  const formatDateKey = (year: number, month: number, day: number): string => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };
  const [selectedDate, setSelectedDate] = useState<string>(() =>
    formatDateKey(today.getFullYear(), today.getMonth(), today.getDate())
  );

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="flex justify-center gap-4">
            <div className="w-[300px] h-64 bg-gray-200 rounded"></div>
            <div className="w-[300px] h-64 bg-gray-200 rounded"></div>
            <div className="w-[420px] h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleIssueNavigate = (event: CalendarEventItem) => {
    setActiveView('issues');
    setExpandedIssueId(event.issueId);
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          {currentProject?.name ? `${currentProject.name} Issue Dashboard` : 'Issue Dashboard'}
        </h3>
      </div>

      <div className="flex justify-center gap-4">
        <div className="flex-1 min-w-[250px]">
          <IssuesCalendarGrid
            productIssues={productIssues}
            productionIssues={productionIssues}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
        </div>

        <div className="flex-1 min-w-[300px]">
          <IssuesCalendarEvents
            productIssues={productIssues}
            productionIssues={productionIssues}
            selectedDate={selectedDate}
            onEventClick={handleIssueNavigate}
          />
        </div>

        <div className="flex-1 min-w-[250px]">
          <IssueDonutChart issues={allIssues} />
        </div>
      </div>
    </div>
  );
}
